import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Factory, PlusCircle } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid, LabelList, ReferenceLine
} from 'recharts';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFechaHora } from '../utils/format';
import PendientesProduccion from '../components/PendientesProduccion';
import BotonAyuda from '../components/BotonAyuda';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];
const MESES_ABREV = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const DIAS_SEMANA = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];
const COLORES_MAQUINA = [
  '#0d9488', '#f59e0b', '#6366f1', '#db2777', '#65a30d',
  '#0284c7', '#ea580c', '#7c3aed', '#c026d3', '#059669'
];
const SEMANAS_GRAFICO = 8;

function pad(n) {
  return String(n).padStart(2, '0');
}

function diasEnMes(year, mes) {
  return new Date(year, mes + 1, 0).getDate();
}

function primerDiaSemana(year, mes) {
  return (new Date(year, mes, 1).getDay() + 6) % 7; // 0 = lunes
}

function parseFecha(fecha) {
  const [y, m, d] = fecha.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function sumarDias(fecha, delta) {
  const dt = parseFecha(fecha);
  dt.setDate(dt.getDate() + delta);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

function fechaStr(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function lunesDeSemana(fecha) {
  const dt = parseFecha(fecha);
  const offset = (dt.getDay() + 6) % 7;
  dt.setDate(dt.getDate() - offset);
  return fechaStr(dt);
}

function formatFechaCorta(fecha) {
  return parseFecha(fecha).toLocaleDateString('es', { day: 'numeric', month: 'short' });
}

function colorCelda(total, max) {
  const intensidad = Math.min(1, total / max);
  if (intensidad < 0.25) return 'bg-teal-50/40';
  if (intensidad < 0.5) return 'bg-teal-50';
  if (intensidad < 0.75) return 'bg-teal-100';
  return 'bg-teal-100';
}

function agruparPorFecha(rows) {
  const mapa = {};
  rows.forEach((r) => {
    const fecha = String(r.fecha_produccion).slice(0, 10);
    if (!mapa[fecha]) {
      mapa[fecha] = {
        total: 0,
        diurno: 0,
        nocturno: 0,
        cargado: { diurno: false, nocturno: false },
        cargadoEn: { diurno: null, nocturno: null },
        estado: { diurno: null, nocturno: null },
        porMaquina: {}
      };
    }
    const total = Number(r.total_kg) || 0;
    mapa[fecha].total += total;
    mapa[fecha][r.turno] += total;
    mapa[fecha].cargado[r.turno] = r.estado !== 'no_trabajado';
    mapa[fecha].cargadoEn[r.turno] = r.creado_en;
    mapa[fecha].estado[r.turno] = r.estado;
    (r.detalle || []).forEach((d) => {
      if (!mapa[fecha].porMaquina[d.maquina_id]) {
        mapa[fecha].porMaquina[d.maquina_id] = { nombre: d.nombre, kg: 0 };
      }
      mapa[fecha].porMaquina[d.maquina_id].kg += Number(d.kg) || 0;
    });
  });
  return mapa;
}

export default function Produccion() {
  const { empresaActiva, modulosActivos } = useAuth();
  const navigate = useNavigate();
  const hoy = new Date();
  const hoyStr = fechaStr(hoy);
  const [mesVista, setMesVista] = useState({ year: hoy.getFullYear(), mes: hoy.getMonth() });
  const [semanaVista, setSemanaVista] = useState(() => lunesDeSemana(fechaStr(hoy)));
  const [celdaAbierta, setCeldaAbierta] = useState(null);
  const [vistaEvolucion, setVistaEvolucion] = useState('semanal');

  const [maquinasActivas, setMaquinasActivas] = useState([]);
  const [datosAnuales, setDatosAnuales] = useState({});
  const [datosMesVista, setDatosMesVista] = useState({});
  const [datosFiscalMes, setDatosFiscalMes] = useState({});
  const [datosSemanaVista, setDatosSemanaVista] = useState({});

  useEffect(() => {
    if (!empresaActiva) return;
    api.get('/produccion/maquinas').then(({ data }) => setMaquinasActivas(data));
  }, [empresaActiva]);

  useEffect(() => {
    if (!empresaActiva) return;
    // Un mes extra hacia atrás para cubrir las ventanas fiscales (16 al 15) de los últimos 12 meses.
    const idx = hoy.getFullYear() * 12 + hoy.getMonth() - 12;
    const year = Math.floor(idx / 12);
    const mes = ((idx % 12) + 12) % 12;
    const desde = `${year}-${pad(mes + 1)}-16`;
    const hasta = fechaStr(hoy);
    api.get('/produccion/rango', { params: { desde, hasta } }).then(({ data }) => setDatosAnuales(agruparPorFecha(data)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  const rangoMesCeldas = useMemo(() => {
    const offset = primerDiaSemana(mesVista.year, mesVista.mes);
    const totalDiasMes = diasEnMes(mesVista.year, mesVista.mes);
    const primerDiaMes = `${mesVista.year}-${pad(mesVista.mes + 1)}-01`;
    const ultimoDiaMes = `${mesVista.year}-${pad(mesVista.mes + 1)}-${pad(totalDiasMes)}`;
    const sobrante = (7 - ((offset + totalDiasMes) % 7)) % 7;
    return { desde: sumarDias(primerDiaMes, -offset), hasta: sumarDias(ultimoDiaMes, sobrante) };
  }, [mesVista]);

  useEffect(() => {
    if (!empresaActiva) return;
    api.get('/produccion/rango', { params: rangoMesCeldas }).then(({ data }) => setDatosMesVista(agruparPorFecha(data)));
  }, [empresaActiva, rangoMesCeldas]);

  // Ventana fiscal del "total mensual": del 16 del mes anterior al 15 del mes visto
  // (el nombre del mes que se muestra corresponde al mes de la fecha final).
  const rangoFiscalMes = useMemo(() => {
    const idxAnterior = mesVista.year * 12 + mesVista.mes - 1;
    const yearAnterior = Math.floor(idxAnterior / 12);
    const mesAnterior = ((idxAnterior % 12) + 12) % 12;
    return {
      desde: `${yearAnterior}-${pad(mesAnterior + 1)}-16`,
      hasta: `${mesVista.year}-${pad(mesVista.mes + 1)}-15`
    };
  }, [mesVista]);

  useEffect(() => {
    if (!empresaActiva) return;
    api.get('/produccion/rango', { params: rangoFiscalMes }).then(({ data }) => setDatosFiscalMes(agruparPorFecha(data)));
  }, [empresaActiva, rangoFiscalMes]);

  useEffect(() => {
    if (!empresaActiva) return;
    const desde = semanaVista;
    const hasta = sumarDias(semanaVista, 6);
    api.get('/produccion/rango', { params: { desde, hasta } }).then(({ data }) => setDatosSemanaVista(agruparPorFecha(data)));
  }, [empresaActiva, semanaVista]);

  function cambiarMes(delta) {
    setCeldaAbierta(null);
    setMesVista(({ year, mes }) => {
      const total = year * 12 + mes + delta;
      return { year: Math.floor(total / 12), mes: ((total % 12) + 12) % 12 };
    });
  }

  function cambiarSemana(delta) {
    setSemanaVista((fecha) => sumarDias(fecha, delta * 7));
  }

  function irACargar(p) {
    navigate(`/produccion/cargar?fecha=${p.fecha}&turno=${p.turno}`);
  }

  async function marcarNoTrabajado(fecha, turno) {
    await api.post('/produccion', { fecha_produccion: fecha, turno, no_trabajado: true });
    const { data } = await api.get('/produccion/rango', { params: rangoMesCeldas });
    setDatosMesVista(agruparPorFecha(data));
  }

  const { totalMes, diasVentanaFiscal } = useMemo(() => {
    let total = 0;
    let dias = 0;
    let cursor = rangoFiscalMes.desde;
    while (cursor <= rangoFiscalMes.hasta) {
      total += datosFiscalMes[cursor]?.total || 0;
      dias += 1;
      cursor = sumarDias(cursor, 1);
    }
    return { totalMes: total, diasVentanaFiscal: dias };
  }, [rangoFiscalMes, datosFiscalMes]);

  const { semanas, maxDia } = useMemo(() => {
    const offset = primerDiaSemana(mesVista.year, mesVista.mes);
    const totalDiasMes = diasEnMes(mesVista.year, mesVista.mes);
    const primerDiaMes = `${mesVista.year}-${pad(mesVista.mes + 1)}-01`;

    const celdas = [];
    for (let i = offset; i > 0; i--) celdas.push({ fecha: sumarDias(primerDiaMes, -i), enMes: false });
    for (let dia = 1; dia <= totalDiasMes; dia++) {
      celdas.push({ fecha: `${mesVista.year}-${pad(mesVista.mes + 1)}-${pad(dia)}`, enMes: true });
    }
    const sobrante = (7 - (celdas.length % 7)) % 7;
    const ultimaFecha = celdas[celdas.length - 1].fecha;
    for (let i = 1; i <= sobrante; i++) celdas.push({ fecha: sumarDias(ultimaFecha, i), enMes: false });

    const filas = [];
    for (let i = 0; i < celdas.length; i += 7) {
      const semana = celdas.slice(i, i + 7).map((c) => {
        const d = datosMesVista[c.fecha] || {
          total: 0,
          diurno: 0,
          nocturno: 0,
          cargado: { diurno: false, nocturno: false },
          cargadoEn: { diurno: null, nocturno: null },
          estado: { diurno: null, nocturno: null }
        };
        return {
          ...c,
          total: d.total,
          diurno: d.diurno,
          nocturno: d.nocturno,
          cargado: d.cargado,
          cargadoEn: d.cargadoEn,
          estado: d.estado
        };
      });
      const totalSemana = semana.reduce((acc, c) => acc + c.total, 0);
      const diurnoSemana = semana.reduce((acc, c) => acc + c.diurno, 0);
      const nocturnoSemana = semana.reduce((acc, c) => acc + c.nocturno, 0);
      filas.push({ dias: semana, total: totalSemana, diurno: diurnoSemana, nocturno: nocturnoSemana });
    }

    const maxDia = Math.max(1, ...filas.flatMap((f) => f.dias.map((d) => d.total)));

    return { semanas: filas, maxDia };
  }, [mesVista, datosMesVista]);

  const datosMeses = useMemo(() => {
    const arr = [];
    for (let i = 11; i >= 0; i--) {
      const idx = hoy.getFullYear() * 12 + hoy.getMonth() - i;
      const year = Math.floor(idx / 12);
      const mes = ((idx % 12) + 12) % 12;
      const idxAnterior = idx - 1;
      const yearAnterior = Math.floor(idxAnterior / 12);
      const mesAnterior = ((idxAnterior % 12) + 12) % 12;
      const desde = `${yearAnterior}-${pad(mesAnterior + 1)}-16`;
      const hasta = `${year}-${pad(mes + 1)}-15`;
      let total = 0;
      let cursor = desde;
      while (cursor <= hasta) {
        total += datosAnuales[cursor]?.total || 0;
        cursor = sumarDias(cursor, 1);
      }
      arr.push({
        label: `${MESES_ABREV[mes]} ${String(year).slice(-2)}`,
        total,
        esActual: year === hoy.getFullYear() && mes === hoy.getMonth()
      });
    }
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datosAnuales]);

  const datosSemanales = useMemo(() => {
    const lunesActual = lunesDeSemana(hoyStr);
    const arr = [];
    for (let i = SEMANAS_GRAFICO - 1; i >= 0; i--) {
      const lunes = sumarDias(lunesActual, -i * 7);
      let total = 0;
      let diurno = 0;
      let nocturno = 0;
      for (let d = 0; d < 7; d++) {
        const dato = datosAnuales[sumarDias(lunes, d)];
        if (dato) {
          total += dato.total;
          diurno += dato.diurno;
          nocturno += dato.nocturno;
        }
      }
      arr.push({ semana: formatFechaCorta(lunes), total, diurno, nocturno });
    }
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datosAnuales, hoyStr]);

  const promedios6Meses = useMemo(() => {
    const dias = [];
    for (let i = 5; i >= 0; i--) {
      const idx = hoy.getFullYear() * 12 + hoy.getMonth() - i;
      const year = Math.floor(idx / 12);
      const mes = ((idx % 12) + 12) % 12;
      for (let dia = 1; dia <= diasEnMes(year, mes); dia++) {
        const fecha = `${year}-${pad(mes + 1)}-${pad(dia)}`;
        if (fecha <= hoyStr) dias.push(fecha);
      }
    }
    let sumaDiurno = 0;
    let sumaNocturno = 0;
    dias.forEach((f) => {
      const d = datosAnuales[f];
      if (d) {
        sumaDiurno += d.diurno;
        sumaNocturno += d.nocturno;
      }
    });
    const n = dias.length || 1;
    return { diurnoSemanal: (sumaDiurno / n) * 7, nocturnoSemanal: (sumaNocturno / n) * 7 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datosAnuales]);

  const desgloseSemana = useMemo(() => {
    const dias = Array.from({ length: 7 }, (_, i) => sumarDias(semanaVista, i));
    return dias.map((fecha) => ({ fecha, porMaquina: datosSemanaVista[fecha]?.porMaquina || {} }));
  }, [semanaVista, datosSemanaVista]);

  const diasSemanaSeleccionada = useMemo(
    () => new Set(desgloseSemana.map((d) => d.fecha)),
    [desgloseSemana]
  );

  const filasPorMaquina = maquinasActivas.map((m, idx) => {
    const valores = desgloseSemana.map((d) => ({ kg: d.porMaquina[m.id]?.kg || 0 }));
    return {
      nombre: m.nombre,
      color: COLORES_MAQUINA[idx % COLORES_MAQUINA.length],
      valores,
      total: valores.reduce((a, v) => a + v.kg, 0)
    };
  });
  const maxKgSemana = Math.max(1, ...filasPorMaquina.flatMap((f) => f.valores.map((v) => v.kg)));

  if (!modulosActivos?.produccion) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center text-slate-500">
        El módulo de Producción no está habilitado para esta empresa.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-slate-800">Producción<BotonAyuda /></h1>
        <button
          onClick={() => navigate('/produccion/cargar')}
          className="flex items-center gap-2 bg-primary-600 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-700"
        >
          <PlusCircle size={16} />
          Cargar producción
        </button>
      </div>

      <PendientesProduccion onSeleccionar={irACargar} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-sm text-slate-500 mb-1">Producción total · {MESES[mesVista.mes]} {mesVista.year}</p>
          <p className="text-2xl font-bold text-slate-800 tabular-nums">{formatMonto(totalMes)} kg</p>
          <p className="text-xs text-slate-400 mt-1">
            Del {formatFechaCorta(rangoFiscalMes.desde)} al {formatFechaCorta(rangoFiscalMes.hasta)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-sm text-slate-500 mb-1">Promedio diario</p>
          <p className="text-2xl font-bold text-slate-800 tabular-nums">
            {formatMonto(Math.round(totalMes / diasVentanaFiscal))} kg
          </p>
          <p className="text-xs text-slate-400 mt-1">Sobre los días del período (16 al 15)</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-sm text-slate-500 mb-1">Promedio semanal</p>
          <p className="text-2xl font-bold text-slate-800 tabular-nums">
            {formatMonto(Math.round((totalMes / diasVentanaFiscal) * 7))} kg
          </p>
          <p className="text-xs text-slate-400 mt-1">Promedio diario proyectado a 7 días</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-5">
        <p className="font-medium text-slate-600 mb-4">Total mensual · últimos 12 meses</p>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={datosMeses} margin={{ top: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `${formatMonto(v)} Kg`} />
              <Bar dataKey="total" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                <LabelList dataKey="total" position="top" fontSize={10} fill="#475569" formatter={formatMonto} />
                {datosMeses.map((d) => (
                  <Cell key={d.label} fill={d.esActual ? '#0d9488' : '#5eead4'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="font-medium text-slate-600">
              Evolución {vistaEvolucion === 'semanal' ? 'semanal' : 'mensual'} (kg)
            </p>
            <div className="flex rounded-md border overflow-hidden text-xs">
              {[
                { valor: 'semanal', label: 'Semanal' },
                { valor: 'mensual', label: 'Mensual' }
              ].map((op) => (
                <button
                  key={op.valor}
                  type="button"
                  onClick={() => setVistaEvolucion(op.valor)}
                  className={`px-3 py-1.5 font-medium transition-colors ${
                    vistaEvolucion === op.valor ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {op.label}
                </button>
              ))}
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={vistaEvolucion === 'semanal' ? datosSemanales : datosMeses}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey={vistaEvolucion === 'semanal' ? 'semana' : 'label'} tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => `${formatMonto(v)} kg`} />
                <Line type="monotone" dataKey="total" name="Total" stroke="#0d9488" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="font-medium text-slate-600 mb-4">Turno diurno vs. nocturno (kg)</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={datosSemanales}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="semana" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => `${formatMonto(v)} kg`} />
                <Legend />
                <Bar dataKey="diurno" name="Diurno" fill="#f59e0b" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                <Bar dataKey="nocturno" name="Nocturno" fill="#312e81" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                <ReferenceLine
                  y={promedios6Meses.diurnoSemanal}
                  stroke="#b45309"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  label={{ value: 'Prom. diurno (6m)', position: 'insideBottomLeft', fontSize: 10, fill: '#b45309' }}
                />
                <ReferenceLine
                  y={promedios6Meses.nocturnoSemanal}
                  stroke="#312e81"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  label={{ value: 'Prom. nocturno (6m)', position: 'insideTopLeft', fontSize: 10, fill: '#312e81' }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => cambiarMes(-1)}
            className="p-2 rounded-md text-slate-500 hover:bg-slate-100 transition-colors"
            title="Mes anterior"
          >
            <ChevronLeft size={18} />
          </button>
          <p className="font-medium text-slate-700 flex items-center gap-2">
            <Factory size={16} className="text-teal-600" />
            {MESES[mesVista.mes]} {mesVista.year}
          </p>
          <button
            onClick={() => cambiarMes(1)}
            className="p-2 rounded-md text-slate-500 hover:bg-slate-100 transition-colors"
            title="Mes siguiente"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="grid grid-cols-8 gap-2 mb-2">
          {DIAS_SEMANA.map((d) => (
            <div key={d} className="text-center text-sm font-semibold text-slate-400 py-1">{d}</div>
          ))}
          <div className="text-center text-sm font-semibold text-slate-400 py-1">Total sem.</div>
        </div>

        {semanas.map((fila, idx) => (
          <div key={idx} className="grid grid-cols-8 gap-2 mb-2">
            {fila.dias.map((c) => (
              <div key={c.fecha} className="relative">
                <button
                  type="button"
                  onClick={() => {
                    if (!c.enMes) return;
                    setCeldaAbierta(celdaAbierta === c.fecha ? null : c.fecha);
                    setSemanaVista(lunesDeSemana(c.fecha));
                  }}
                  className={`w-full rounded-lg border p-2 min-h-[68px] flex flex-col justify-between text-left
                    ${c.enMes ? colorCelda(c.total, maxDia) : 'bg-slate-50/50 opacity-50 cursor-default'}
                    ${c.enMes && diasSemanaSeleccionada.has(c.fecha) ? 'border-teal-300' : c.enMes ? 'border-slate-100' : 'border-slate-50'}
                    ${celdaAbierta === c.fecha ? 'ring-1 ring-primary-500' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <span className={`text-sm font-semibold ${c.enMes ? 'text-slate-600' : 'text-slate-400'}`}>
                      {Number(c.fecha.slice(-2))}
                    </span>
                    {c.enMes && c.fecha <= hoyStr && (
                      <span className="flex flex-col gap-1 mt-0.5">
                        {['diurno', 'nocturno'].map((turno) => (
                          <span
                            key={turno}
                            title={`Turno ${turno}: ${
                              c.estado?.[turno] === 'no_trabajado'
                                ? 'no trabajado'
                                : c.cargado?.[turno]
                                  ? 'cargado'
                                  : 'sin cargar'
                            }`}
                            className={`h-1.5 w-1.5 rounded-full ${
                              c.estado?.[turno] === 'no_trabajado'
                                ? 'bg-slate-400'
                                : c.cargado?.[turno]
                                  ? 'bg-green-500'
                                  : 'bg-red-500'
                            }`}
                          />
                        ))}
                      </span>
                    )}
                  </div>
                  <span className={`text-xs font-medium tabular-nums self-end ${c.enMes ? 'text-slate-700' : 'text-slate-400'}`}>
                    {formatMonto(c.total)}
                  </span>
                </button>

                {celdaAbierta === c.fecha && (
                  <div className="absolute z-20 top-full left-0 mt-1 w-60 rounded-lg border border-slate-200 bg-white p-3 shadow-lg text-xs space-y-2">
                    <p className="font-semibold text-slate-700">{formatFechaCorta(c.fecha)}</p>
                    {['diurno', 'nocturno'].map((turno) => (
                      <div key={turno} className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-slate-500 capitalize">{turno}</p>
                          <p className="text-slate-700">
                            {c.estado?.[turno] === 'no_trabajado'
                              ? 'No trabajado'
                              : c.cargado?.[turno]
                                ? `Cargado el ${formatFechaHora(c.cargadoEn?.[turno])}`
                                : 'Sin cargar'}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {c.estado?.[turno] !== 'no_trabajado' && (
                            <button
                              type="button"
                              onClick={() => navigate(`/produccion/cargar?fecha=${c.fecha}&turno=${turno}`)}
                              className="flex-shrink-0 text-teal-700 font-medium hover:underline"
                            >
                              {c.cargado?.[turno] ? 'Editar' : 'Cargar'}
                            </button>
                          )}
                          {c.estado?.[turno] == null && (
                            <button
                              type="button"
                              onClick={() => marcarNoTrabajado(c.fecha, turno)}
                              className="flex-shrink-0 text-slate-500 font-medium hover:underline"
                            >
                              No trabajado
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="rounded-lg bg-teal-600 shadow-sm p-2 min-h-[68px] flex flex-col justify-center items-center">
              <span className="text-xs text-teal-100 font-semibold">Total</span>
              <span className="text-sm font-bold text-white tabular-nums">{formatMonto(fila.total)}</span>
            </div>
          </div>
        ))}
      </div>

      {filasPorMaquina.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-1">
            <button
              onClick={() => cambiarSemana(-1)}
              className="p-2 rounded-md text-slate-500 hover:bg-slate-100 transition-colors"
              title="Semana anterior"
            >
              <ChevronLeft size={18} />
            </button>
            <p className="font-medium text-slate-700 flex items-center gap-2">
              <Factory size={16} className="text-teal-600" />
              Producción por máquina · Semana del {formatFechaCorta(desgloseSemana[0].fecha)} al {formatFechaCorta(desgloseSemana[6].fecha)}
            </p>
            <button
              onClick={() => cambiarSemana(1)}
              className="p-2 rounded-md text-slate-500 hover:bg-slate-100 transition-colors"
              title="Semana siguiente"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <p className="text-xs text-slate-400 mb-4 mt-2">
            Cada fila es una máquina: comparar la longitud de sus barras muestra su producción a lo largo de la semana.
          </p>

          <div className="overflow-x-auto">
            <div style={{ minWidth: 720 }}>
              <div className="grid gap-2" style={{ gridTemplateColumns: '110px repeat(7, 1fr) 80px' }}>
                <div />
                {desgloseSemana.map(({ fecha }) => (
                  <div key={fecha} className="text-center text-xs font-semibold text-slate-400 pb-1">
                    {formatFechaCorta(fecha)}
                  </div>
                ))}
                <div className="text-center text-xs font-semibold text-slate-400 pb-1">Total sem.</div>
              </div>

              {filasPorMaquina.map((fila) => (
                <div
                  key={fila.nombre}
                  className="grid gap-2 items-center py-1"
                  style={{ gridTemplateColumns: '110px repeat(7, 1fr) 80px' }}
                >
                  <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 truncate">
                    <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: fila.color }} />
                    {fila.nombre}
                  </div>
                  {fila.valores.map((v, idx) => (
                    <div
                      key={desgloseSemana[idx].fecha}
                      title={`${fila.nombre} · ${formatFechaCorta(desgloseSemana[idx].fecha)}: ${formatMonto(v.kg)} kg`}
                      className="h-5 rounded bg-slate-100 overflow-hidden"
                    >
                      <div
                        className="h-full rounded flex items-center justify-start px-1"
                        style={{ width: `${Math.max(v.kg > 0 ? 8 : 0, (v.kg / maxKgSemana) * 100)}%`, backgroundColor: fila.color }}
                      >
                        {v.kg > 0 && (
                          <span className="text-[9px] font-semibold text-white tabular-nums leading-none whitespace-nowrap">
                            {formatMonto(v.kg)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  <div
                    className="rounded bg-teal-600 py-1 text-center"
                    title={`${fila.nombre} · total de la semana: ${formatMonto(fila.total)} kg`}
                  >
                    <span className="text-[10px] font-bold text-white tabular-nums">{formatMonto(fila.total)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
